import ReactApexChart from "react-apexcharts";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { useThemeStore } from "../../../store";
import { useTheme } from "../../../hooks";
import { themes } from "../../../config/thems";
import { useMemo } from "react";
import Utility from "../../../helper/utilityHelper";
import LottieEmptyState from "./no-data-animation/index";

const PaidUnpaid = ({ bookingSummary, height = 200 }) => {
  const { theme: config } = useThemeStore();
  const { theme: mode } = useTheme();
  const theme = themes.find((theme) => theme.name === config);

  // Parse the JSON string data
  const parsedData = useMemo(() => {
    if (!bookingSummary) return {};
    
    try {
      let result;
      if (typeof bookingSummary === 'string') {
        result = JSON.parse(bookingSummary);
      } else {
        result = bookingSummary;
      }
      return result;
    } catch (error) {
      console.error('Error parsing paidUnpaid data:', error);
      return {};
    }
  }, [bookingSummary]);

  const totalEarnings =
    (parsedData?.appEarnings || 0) +
    (parsedData?.clubEarnings || 0) +
    (parsedData?.myAppEarnings || 0) +
    (parsedData?.webEarnings || 0) +
    (parsedData?.whatsappEarnings || 0);
  
  const totalReceivedAmount = parsedData?.totalReceivedAmount || 0;
  const receivableAmount = totalEarnings - totalReceivedAmount;

  // Process data for donut chart - same logic as dashboardOld
  const series = useMemo(() => {
    const totalUnpaidAmount = parsedData?.totalUnpaidAmount || 0;
    return [totalReceivedAmount, totalUnpaidAmount];
  }, [parsedData]);

  const labels = ["Paid", "Unpaid"];

  // Check if there's no data to display
  const hasNoData = totalEarnings === 0 && totalReceivedAmount === 0;

  const options = {
    chart: {
      toolbar: {
        show: false,
      },
    },
    plotOptions: {
      pie: {
        startAngle: -90,
        endAngle: 270,
        donut: {
          size: '70%',
        },
      },
    },
    dataLabels: {
      enabled: false,
    },
    fill: {
      type: "solid",
    },
    colors: [
      `hsl(${theme?.cssVars[mode === "dark" ? "dark" : "light"].success})`, // Green for Paid
      `hsl(${theme?.cssVars[mode === "dark" ? "dark" : "light"].destructive})`, // Red for Unpaid
    ],
    legend: {
      position: "bottom",
      formatter: function (val, opts) {
        const value = opts.w.globals.series[opts.seriesIndex];
        return val + " - " + Utility.formatNumber(value);
      },
      labels: {
        colors: `hsl(${
          theme?.cssVars[
            mode === "dark" || mode === "system" ? "dark" : "light"
          ].chartLabel
        })`,
      },
    },
    stroke: {
      width: 0,
    },
    tooltip: {
      theme: mode === "dark" || mode === "system" ? "dark" : "light",
      y: {
        formatter: function (val) {
          return Utility.formatNumberWithCommas(val);
        },
      },
    },
    labels: labels,
    padding: {
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },
  };

  return (
    <Card className="h-full overflow-visible">
      <CardHeader className="flex-row justify-between items-center mb-0 border-none pt-4 md:pt-8 pl-3 md:pl-6 overflow-visible">
        <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 relative w-full overflow-visible">
          <CardTitle className="text-lg md:text-2xl font-medium">
            Receivable ({Utility.formatNumber(receivableAmount)})
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="px-0 pb-6">
        {hasNoData ? (
          <LottieEmptyState
            title="No Payment Data"
            subtitle="Payment information will appear here when available"
            height={320}
            width={200}
          />
        ) : (
          <div className="w-full max-w-[300px] mx-auto">
            <ReactApexChart
              options={options}
              series={series}
              type="donut"
              height={320}
              width={"100%"}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PaidUnpaid;